<template>
  <div class="view-report-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-wrapper">
      <div class="loading-content">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <span class="loading-text">加载中...</span>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-wrapper">
      <div class="error-content">
        <el-icon class="error-icon"><Warning /></el-icon>
        <h3 class="error-title">{{ error }}</h3>
      </div>
    </div>

    <!-- 报告内容 -->
    <div v-else-if="report" class="report-content">
      <!-- 页面标题 -->
      <div class="page-title">{{ report.sampleName || '检验报告详情' }}</div>
      
      <!-- 蓝色分割线 -->
      <div class="title-divider"></div>

      <!-- 报告信息 -->
      <div class="info-section">
        <div class="info-row">
          <span class="info-label">样品名称：</span>
          <span class="info-value">{{ report.sampleName || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">报告编号：</span>
          <span class="info-value">{{ report.sampleNumber || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">检验状态：</span>
          <span class="info-value">{{ report.inspectionStatus || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">检验类型：</span>
          <span class="info-value">{{ report.inspectionType || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">样品状态：</span>
          <span class="info-value">{{ report.sampleStatus || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">委托单位：</span>
          <span class="info-value">{{ report.clientUnit || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">受检单位：</span>
          <span class="info-value">{{ report.inspectedUnit || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">生产单位：</span>
          <span class="info-value">{{ report.manufacturerUnit || '/' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">完成期限：</span>
          <span class="info-value">{{ report.completionDeadline ? formatDate(report.completionDeadline) : '/' }}</span>
        </div>
        <div class="info-row" v-if="report.attachment && report.attachment.fileName">
          <span class="info-label">报告附件：</span>
          <span class="info-value">{{ report.attachment.fileName }}</span>
        </div>
      </div>

      

      <!-- 查看报告按钮 -->
      <div class="action-section">
        <el-button 
          type="warning" 
          size="large" 
          class="view-report-btn"
          @click="viewSourceFile"
          :disabled="!report"
        >
          报告查看
        </el-button>
        <el-button 
          v-if="false"
          type="primary" 
          size="large" 
          class="download-report-btn"
          @click="downloadReport"
          :disabled="!report"
        >
          下载报告
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Loading, Warning, Document } from '@element-plus/icons-vue'

export default {
  name: 'ViewReport',
  components: {
    Loading,
    Warning,
    Document
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      report: null,
      loading: true,
      error: null,
      showDownloadButton: false, // 下载按钮逻辑暂时隐藏
    }
  },

  created() {
    // 已移除事件机制：开关状态改为从后端设置加载
  },
  async mounted() {
    await this.loadReport()
  },
  methods: {
    async loadReport() {
      try {
        this.loading = true
        // 添加时间戳防缓存
        const timestamp = Date.now()
        const response = await axios.get(`/api/report/${this.id}?_t=${timestamp}`)
        
        if (response.data.success) {
          this.report = response.data.data
        } else {
          this.error = response.data.error || '加载报告失败'
        }
      } catch (error) {
        console.error('加载报告错误:', error)
        if (error.response && error.response.status === 404) {
          this.error = '报告不存在'
        } else {
          this.error = '网络错误，请稍后重试'
        }
      } finally {
        this.loading = false
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    
    getStatusClass(status) {
      if (!status) return ''
      if (status.includes('已检') || status.includes('合格')) {
        return 'status-success'
      } else if (status.includes('不合格') || status.includes('失败')) {
        return 'status-danger'
      } else if (status.includes('检验中') || status.includes('进行中')) {
        return 'status-warning'
      }
      return 'status-info'
    },
    
    viewSourceFile() {
      if (this.report) {
        // 直接在新窗口打开PDF文件，添加时间戳防缓存
        const timestamp = Date.now()
        const pdfUrl = `/api/proxy-file/${this.report.id}?_t=${timestamp}`
        window.open(pdfUrl, '_blank')
      }
    },
    
    downloadReport() {
      if (this.report) {
        // 创建一个临时的a标签来触发下载
        const link = document.createElement('a')
        link.href = `/api/download-file/${this.report.id}`
        link.download = this.report.fileName || `report-${this.report.id}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    
    editReport() {
      if (this.report) {
        // 跳转到编辑页面
        this.$router.push({ name: 'EditReport', params: { id: this.report.id } })
      }
    },

    // 下载按钮逻辑暂时关闭
  }
}
</script>

<style scoped>
.view-report-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* 加载状态样式 */
.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.loading-icon {
  font-size: 32px;
  color: #409eff;
}

.loading-text {
  font-size: 16px;
  color: #606266;
  font-weight: 500;
}

/* 错误状态样式 */
.error-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-icon {
  font-size: 32px;
  color: #f56c6c;
}

.error-title {
  margin: 0;
  color: #303133;
  font-weight: 500;
}

/* 报告内容样式 */
.report-content {
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 40px;
  margin-top: 20px;
}

/* 页面标题 */
.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  text-align: center;
  margin-bottom: 30px;
}

/* 蓝色分割线 */
.title-divider {
  width: 100%;
  height: 3px;
  background: #4a90e2;
  margin-bottom: 40px;
}

/* 信息区域 */
.info-section {
  margin-bottom: 50px;
}

.info-row {
  display: flex;
  margin-bottom: 20px;
  line-height: 1.6;
}

.info-label {
  font-size: 14px;
  color: #666;
  min-width: 100px;
  font-weight: 600;
  margin-right: 8px;
}

.info-value {
  font-size: 16px;
  color: #333;
  flex: 1;
  font-weight: 400;
}

/* 操作按钮区域 */
.action-section {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 20px;
}

.view-report-btn {
  padding: 12px 40px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 6px;
  background: #f39c12;
  border: none;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.view-report-btn:hover {
  background: #e67e22;
}

.view-report-btn:active {
  background: #d35400;
}

.download-report-btn {
  padding: 12px 40px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 6px;
  background: #409eff;
  border: none;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.download-report-btn:hover {
  background: #337ecc;
}

.download-report-btn:active {
  background: #2b6cb0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .view-report-container {
    padding: 16px;
  }
  
  .report-content {
    padding: 30px 20px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .info-label {
    min-width: 80px;
    font-size: 13px;
    font-weight: 600;
  }
  
  .info-value {
    font-size: 15px;
    font-weight: 400;
  }
  
  .view-report-btn,
  .download-report-btn {
    padding: 10px 30px;
    font-size: 14px;
  }
  
  .action-section {
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .view-report-container {
    padding: 12px;
  }
  
  .report-content {
    padding: 20px 16px;
  }
  
  .page-title {
    font-size: 18px;
  }
  
  .info-row {
    flex-direction: row;
    margin-bottom: 15px;
  }
  
  .info-label {
    min-width: 70px;
    margin-bottom: 0;
    margin-right: 6px;
    font-size: 12px;
    font-weight: 600;
  }
  
  .info-value {
    font-size: 13px;
    font-weight: 400;
    flex: 1;
  }
  
  .view-report-btn,
  .download-report-btn {
    padding: 8px 20px;
    font-size: 13px;
  }
  
  .action-section {
    gap: 8px;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
}
</style>
